import { useState, useCallback } from 'react';

/**
 * Highlighter color palette from user specification
 * Ranked by commonality/preference for physical highlighters
 */
export const HIGHLIGHTER_COLORS = [
  { rank: 1, name: 'Yellow', hex: '#FFE119', rgb: 'R:255,G:225,B:25' },
  { rank: 2, name: 'Green', hex: '#3CB44B', rgb: 'R:60,G:180,B:75' },
  { rank: 3, name: 'Pink', hex: '#FABED4', rgb: 'R:250,G:190,B:212' },
  { rank: 4, name: 'Orange', hex: '#F58231', rgb: 'R:245,G:130,B:49' },
  { rank: 5, name: 'Blue', hex: '#4363D8', rgb: 'R:67,G:99,B:216' },
  { rank: 6, name: 'Purple', hex: '#911EB4', rgb: 'R:145,G:30,B:180' },
  { rank: 7, name: 'Magenta', hex: '#F032E6', rgb: 'R:240,G:50,B:230' },
  { rank: 8, name: 'Cyan', hex: '#42D4F4', rgb: 'R:66,G:212,B:244' },
  { rank: 9, name: 'Teal', hex: '#469990', rgb: 'R:70,G:153,B:144' },
  { rank: 10, name: 'Lime', hex: '#BFEF45', rgb: 'R:191,G:239,B:69' },
  { rank: 11, name: 'Red', hex: '#E6194B', rgb: 'R:230,G:25,B:75' },
];

export interface ActiveHighlight {
  modelId: string;
  color: string;
  colorIndex: number;
}

export interface UseHighlightingReturn {
  activeHighlights: Map<string, ActiveHighlight>;
  toggleModelHighlight: (modelId: string, assignedColor?: string) => void;
  toggleIonoffHighlight: () => void;
  getModelColor: (modelId: string) => string | undefined;
  isModelActive: (modelId: string) => boolean;
  isIonoffActive: boolean;
  ionoffColor: string | undefined;
  hasAnyActive: boolean;
  clearAll: () => void;
  HIGHLIGHTER_COLORS: typeof HIGHLIGHTER_COLORS;
}

/**
 * Custom hook for managing LCS highlighting state
 * 
 * Manages which model chips are active and assigns colors sequentially
 * When models are active, ionoff gets the "next unused color"
 */
export function useHighlighting(): UseHighlightingReturn {
  const [activeHighlights, setActiveHighlights] = useState<Map<string, ActiveHighlight>>(
    new Map()
  );

  const toggleModelHighlight = useCallback((modelId: string, assignedColor?: string) => {
    console.log('🎨 Toggling highlight for model:', modelId, 'assignedColor:', assignedColor);
    setActiveHighlights((prev) => {
      const newMap = new Map(prev);

      if (newMap.has(modelId)) {
        // Deactivate: remove the highlight
        console.log('  → Deactivating (was active)');
        newMap.delete(modelId);
        
        // If this was the last model, also deactivate ionoff
        if (newMap.size === 1 && newMap.has('ionoff')) {
          console.log('  → Also deactivating ionoff (no models left)');
          newMap.delete('ionoff');
        }
      } else {
        // Activate: use assigned color (fixed) or fallback to next available
        if (assignedColor) {
          // Use the fixed color assignment that matches the chip
          const colorIndex = HIGHLIGHTER_COLORS.findIndex(c => c.hex === assignedColor);
          console.log(`  → Activating with ASSIGNED color: ${assignedColor} (index: ${colorIndex})`);
          newMap.set(modelId, {
            modelId,
            color: assignedColor,
            colorIndex: colorIndex >= 0 ? colorIndex : 0,
          });
          
          // Auto-activate ionoff when any model is activated
          if (!newMap.has('ionoff')) {
            const ionoffColorIndex = HIGHLIGHTER_COLORS.findIndex(c => c.name === 'Yellow');
            console.log('  → Auto-activating ionoff');
            newMap.set('ionoff', {
              modelId: 'ionoff',
              color: HIGHLIGHTER_COLORS[ionoffColorIndex].hex,
              colorIndex: ionoffColorIndex,
            });
          }
        } else {
          // Fallback to dynamic assignment
          const usedIndices = Array.from(newMap.values()).map((h) => h.colorIndex);
          const nextIndex = HIGHLIGHTER_COLORS.findIndex(
            (_, idx) => !usedIndices.includes(idx)
          );

          if (nextIndex !== -1) {
            const color = HIGHLIGHTER_COLORS[nextIndex];
            console.log(`  → Activating with next available color: ${color.name} (${color.hex})`);
            newMap.set(modelId, {
              modelId,
              color: color.hex,
              colorIndex: nextIndex,
            });
          }
        }
      }

      console.log('  → Active highlights:', Array.from(newMap.keys()));
      return newMap;
    });
  }, []);

  const getModelColor = useCallback(
    (modelId: string): string | undefined => {
      return activeHighlights.get(modelId)?.color;
    },
    [activeHighlights]
  );

  const isModelActive = useCallback(
    (modelId: string): boolean => {
      return activeHighlights.has(modelId);
    },
    [activeHighlights]
  );

  // Ionoff is treated as a special model with ID "ionoff"
  const toggleIonoffHighlight = useCallback(() => {
    console.log('🎨 Toggling ionoff highlight');
    toggleModelHighlight('ionoff');
  }, [toggleModelHighlight]);

  const isIonoffActive = activeHighlights.has('ionoff');
  const ionoffColor = activeHighlights.get('ionoff')?.color;

  const hasAnyActive = activeHighlights.size > 0;

  const clearAll = useCallback(() => {
    setActiveHighlights(new Map());
  }, []);

  return {
    activeHighlights,
    toggleModelHighlight,
    toggleIonoffHighlight,
    getModelColor,
    isModelActive,
    isIonoffActive,
    ionoffColor,
    hasAnyActive,
    clearAll,
    HIGHLIGHTER_COLORS,
  };
}

